import type { HourColumn, TaskColumn } from 'constants/export';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import type {
  Account,
  AccountStatus,
  AccountUserProperty,
  ClientSideAccountUserProperty,
  Experiment,
  Features,
  RawAddOn,
  SubscriptionPlan,
  UserAccountProperty,
} from 'types/account';
import { actionCreatorFactory } from 'typescript-fsa';
import * as z from 'zod';

export interface OnboardingFormData {
  workspaceName?: string;
  peopleSize?: string;
  useCase?: string;
  industry?: string;
  jobTitle?: string;
  projects?: Array<{
    localId: string;
    name: string;
    clientName: string;
    color: string;
    description?: string;
    external_id?: string;
    showExternalId?: boolean;
    isPersisted?: boolean;
    // Legacy fallback to hydrate drafts saved before localId rename
    id?: string;
  }>;
  selectedUseCase?: 'billable-time' | 'internal-time';
  isInstallingMemory?: boolean;
  hasConnectedMemory?: boolean;
  /** Set when the user completes MemoryStep's opt-out flow ("Continue without installing").
   * Read by CalendarStep to reframe its pitch as the fallback for automatic capture instead of
   * repeating a cold, unrelated ask right after a decline — see TT-5817. */
  hasSkippedMemoryInstall?: boolean;
  /** Epoch ms of the last triggerProjectSuggestions call. */
  lastSuggestionsTriggeredAt?: number;
  /** Trigger timestamp the current `projects` were derived from; stale when `< lastSuggestionsTriggeredAt`. */
  projectsSourceTriggeredAt?: number;
}

export interface State {
  current: Account;
  entries: Array<Account>;
  draft: Account | null;
  onboardingDraft: OnboardingFormData | null;
  emulatedProperties?: {
    beta?: boolean;
    experiments?: Array<Experiment>;
    features?: Features;
    plan?: SubscriptionPlan;
    plan_custom?: boolean;
    status?: AccountStatus;
    next_charge?: string;
    created_at?: number;
  };
  onboardingProperties: Record<AcceptedAccountOnboardingProperties, AccountPropertyRaw> | null;
  xhrStatus: XhrStatus;
  subscriptionPlans: Array<SubscriptionPlan>;
  subscriptionAddOns: Array<RawAddOn>;
  isShowingAddOnsModal: boolean;
  isShowingBookDemoModal: boolean;
  accountReportSettings: AccountReportSettingsPayload | null;
  accountReportSettingsDraft: AccountReportSettingsPayload | null;
  accountUserProperties: Array<AccountUserProperty>;
  accountUserPropertiesDraft: Array<ClientSideAccountUserProperty>;
  resourcePermissions: ResourcePermissionsState | null;
  resourcePermissionDraft: ResourcePermissionsPayload | null;
  userAccountProperties: Record<number, Array<UserAccountProperty>>;
  userPropertiesPreviouslyUsedValues: UserPropertiesPreviouslyUsedValues;
}

export enum AcceptedAccountOnboardingProperties {
  CompanySize = 'company_size',
  EstimatedCompanySize = 'estimated_company_size',
  Industry = 'industry',
  HasTrackedSignUpOnFrontEnd = 'has_tracked_signup_on_fe',
  OnboardingUseCase = 'onboarding_use_case',
  OnboardingJobTitle = 'onboarding_job_title',
}

export enum AccountOffboardingProperties {
  CancellationReason = 'cancellation_reason',
  CancellationFeedback = 'cancellation_feedback',
}

export interface AccountPropertyRaw {
  account_id?: number;
  property: AcceptedAccountOnboardingProperties | AccountOffboardingProperties;
  value: string;
}

export const columnModeOptions = z.enum(['all', 'tags', 'parent_tags']);
export type ColumnModeOptions = z.infer<typeof columnModeOptions>;

export interface AccountReportSettingsPayload {
  account_id: number;
  multiple_timestamps_enabled: boolean;
  hours_column_order: Array<HourColumn>;
  tasks_column_order: Array<TaskColumn>;
  show_excluded_columns: boolean;
  tags_mode: ColumnModeOptions;
}

export type AccountReportSettingsDraft = AccountReportSettingsPayload & {
  hours_column_order: Array<HourColumn>;
};

const crudOperation = z.enum(['create', 'update', 'read', 'delete']);
export type CrudOperation = z.infer<typeof crudOperation>;

const accountResourceTypes = z.enum([
  'user',
  'project',
  'label',
  'company',
  'team',
  'account',
  'report',
  'forecast',
  'invoice',
  'day_property',
]);

export type AccountResourceType = z.infer<typeof accountResourceTypes>;

export const ResourcePermissionsSchema = z.object({
  resource: accountResourceTypes,
  permissions: z.record(z.number({ description: 'role id' }), crudOperation.array()),
});

export type ResourcePermissionsPayload = z.infer<typeof ResourcePermissionsSchema>;

// why can't we have the same format for GET/POST?
export type ResourcePermissionsUpdatePayload = {
  resource: AccountResourceType;
  permissions: Array<{ role_id: number; operations: Array<CrudOperation> }>;
};

export type ResourcePermissions = Record<number, Array<CrudOperation>>; // number => role_id
export type ResourcePermissionsState = { [_K in AccountResourceType]: ResourcePermissions };

export enum AccountSurveys {
  cancel_subscription = 'cancel_subscription',
  delete_account = 'delete_account',
}

export enum AccountSurveyOutcomes {
  isCancelling = 'is_cancelling',
  talkToSupport = 'talk_to_support',
  compareNewPlans = 'compare_new_plans',
}

export type AccountSurveyTypes = keyof typeof AccountSurveys;

const actionCreator = actionCreatorFactory('timely/account');

export type OnboardingPropertyPayload = Record<
  AcceptedAccountOnboardingProperties,
  AccountPropertyRaw
>;
export const fetched = actionCreator<{
  entries: Array<Account>;
  currentAccountId: number;
}>('FETCHED');
export const saved = actionCreator<Account>('SAVED');
export const startDraft = actionCreator<void | Partial<Account>>('START_DRAFT');
export const clearDraft = actionCreator('CLEAR_DRAFT');
export const updateDraft = actionCreator<Partial<Account>>('UPDATE_DRAFT');
export const startReportSettingsDraft = actionCreator<Partial<AccountReportSettingsPayload>>(
  'START_REPORT_SETTINGS_DRAFT'
);
export const createAccountUserPropertiesDraft = actionCreator<void>(
  'START_ACCOUNT_USER_PROPERTIES_DRAFT'
);
export const updateReportSettingsDraft = actionCreator<Partial<AccountReportSettingsPayload>>(
  'UPDATE_REPORT_SETTINGS_DRAFT'
);
export const updateAccountUserPropertiesDraft = actionCreator<
  Partial<AccountReportSettingsPayload>
>('UPDATE_ACCOUNT_USER_PROPERTIES_DRAFT');
export const emulateFeatures =
  actionCreator<Partial<State['emulatedProperties']>>('EMULATE_FEATURES');
export const stopEmulatingFeatures = actionCreator('STOP_EMULATING_FEATURES');
export const setAccountProperties =
  actionCreator<Array<AccountPropertyRaw>>('SET_ACCOUNT_PROPERTIES');
export const setAccountReportSettings = actionCreator<AccountReportSettingsPayload>(
  'SET_ACCOUNT_REPORT_SETTINGS'
);
export const setAccountUserProperties = actionCreator<Array<AccountUserProperty>>(
  'SET_ACCOUNT_USER_PROPERTIES'
);
export const setUserAccountProperties = actionCreator<{
  userId: number;
  properties: Array<UserAccountProperty>;
}>('SET_USER_ACCOUNT_PROPERTIES');
export const deleteDraftAccountUserProperty = actionCreator<number>(
  'DELETE_DRAFT_ACCOUNT_USER_PROPERTY'
);
export const addDraftAccountUserProperty = actionCreator<number>('ADD_DRAFT_ACCOUNT_USER_PROPERTY');

export const updateDraftAccountUserProperty = actionCreator<ClientSideAccountUserProperty>(
  'UPDATE_DRAFT_ACCOUNT_USER_PROPERTY'
);

export type UserPropertiesPreviouslyUsedValues = Record<number, Array<string>>;

export const setAccountUserPropertiesPreviouslyUsedValues =
  actionCreator<UserPropertiesPreviouslyUsedValues>(
    'SET_ACCOUNT_USER_PROPERTIES_PREVIOUSLY_USED_VALUES'
  );

export const fetchedSubscriptionPlans = actionCreator<Array<SubscriptionPlan>>(
  'FETCHED_SUBSCRIPTION_PLANS'
);
export const fetchedSubscriptionAddOns = actionCreator<Array<RawAddOn>>(
  'FETCHED_SUBSCRIPTION_ADD_ONS'
);

export const removeSubscriptionAddOn = actionCreator<RawAddOn>('REMOVE_SUBSCRIPTION_ADD_ON');
export const addSubscriptionAddOn = actionCreator<RawAddOn>('ADD_SUBSCRIPTION_ADD_ON');

export const toggleBookDemoModal = actionCreator('TOGGLE_BOOK_DEMO_MODAL');

export const fetchedSingleResourcePermissions = actionCreator<ResourcePermissionsPayload>(
  'FETCHED_SINGLE_RESOURCE_PERMISSIONS'
);

export const createResourcePermissionsDraft = actionCreator<ResourcePermissionsPayload>(
  'CRATE_RESOURCE_PERMISSIONS_DRAFT'
);

export const updateResourcePermissionsDraft = actionCreator<ResourcePermissionsPayload>(
  'UPDATE_RESOURCE_PERMISSIONS_DRAFT'
);

export const resetResourcePermissionsDraft = actionCreator('RESET_RESOURCE_PERMISSIONS_DRAFT');

export const startOnboardingDraft =
  actionCreator<Partial<OnboardingFormData>>('START_ONBOARDING_DRAFT');
export const updateOnboardingDraft =
  actionCreator<Partial<OnboardingFormData>>('UPDATE_ONBOARDING_DRAFT');
export const clearOnboardingDraft = actionCreator('CLEAR_ONBOARDING_DRAFT');
